/**
 * انتخابِ جواب به یه دعوت — قرینه‌ی RespondSheet.tsx موبایل.
 *
 * تنها فرقِ واقعی با موبایل، شکلِ ظرفه: روی گوشی از پایینِ صفحه بالا می‌آد
 * (bottom sheet)، اینجا یه منوی کوچیک کنارِ خودِ دکمه باز می‌شه. گزینه‌ها،
 * ترتیبشون، و کاری که هر کدوم می‌کنن دقیقاً یکیه.
 *
 * منطقِ برچسب‌ها اینجا نیست — از `@/core/lib/answerLabel` می‌آد، همون فایلی
 * که موبایل هم ازش می‌خونه.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { signalRepo } from '@/core/data/signalRepo';
import { useSession } from '@/core/session';
import { t } from '@/core/lib/i18n';
import { answerShort } from '@/core/lib/answerLabel';
import { describeError, reportError } from '@/core/lib/report';
import { trackFunnel } from '@/core/lib/analytics';
import type { Condition, DeclineReason, ResponseKind, Signal } from '@/core/domain/types';

type Pick = { r: ResponseKind; c?: Condition; why?: DeclineReason };

export function RespondMenu({ signal }: { signal: Signal }) {
  const { profile } = useSession();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [condMode, setCondMode] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const wrap = useRef<HTMLDivElement>(null);

  const answered = !!signal.myResponse && signal.myResponse !== 'pending';

  // کلیک بیرون و کلیدِ Escape هر دو می‌بندن — روی وب کاربر هر دو رو انتظار داره.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  });

  const close = () => { setOpen(false); setCondMode(false); setNote(''); };

  const respond = useMutation({
    mutationFn: async (args: Pick) => {
      if (!profile) return;
      await signalRepo.respond(signal.id, profile.id, args.r, args.c, args.why);
    },
    // همون کلیدهایی که موبایل باطل می‌کنه — فید و خودِ سیگنال از این داده می‌خونن.
    onSuccess: (_d, args) => {
      trackFunnel('rsvp', { id: signal.id, source: 'signal', response: args.r });
      qc.invalidateQueries({ queryKey: ['incoming'] });
      qc.invalidateQueries({ queryKey: ['signal', signal.id] });
      // WHOSUP-342: برچسبِ «هستم/شاید/…» در «برنامه‌های من» از همین جواب است.
      qc.invalidateQueries({ queryKey: ['myPlans'] });
    },
    onError: (e) => {
      // دو خطای معنی‌دارِ دیتابیس (مایگریشن 0131) پیامِ آدمیزادی می‌گیرند.
      // (describeError، چون خطای supabase-js شیءِ ساده است، نه Error.)
      const msg = describeError(e);
      if (msg.includes('capacity_full')) { setError(t('capacityFull')); return; }
      if (msg.includes('deadline passed')) { setError(t('deadlinePassed')); return; }
      reportError('web.signal.respond', e);
      setError(t('errorRetry'));
    },
  });

  const pick = (args: Pick) => { setError(null); close(); respond.mutate(args); };

  const CONDITIONS: Condition[] = [
    { type: 'later', label: t('condLater') },
    { type: 'day', label: t('condDay') },
    { type: 'with_person', label: t('condPerson') },
  ];

  const custom = () => {
    const label = note.trim();
    if (label) pick({ r: 'conditional', c: { type: 'other', label } });
  };

  return (
    <div className="respond" ref={wrap}>
      <button
        type="button"
        className={`answer-btn${answered ? '' : ' wants-you'}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {respond.isPending ? '…' : answerShort(signal)}
        <span aria-hidden="true">›</span>
      </button>

      {error ? <span className="error">{error}</span> : null}

      {open ? (
        <div className="menu" role="menu">
          {condMode ? (
            <>
              <p className="menu-title">{t('imInIf')}</p>
              {CONDITIONS.map((c) => (
                <button key={c.type} type="button" role="menuitem" className="menu-item"
                  onClick={() => pick({ r: 'conditional', c })}>
                  {c.label}
                </button>
              ))}
              {/* شرط‌های آماده همه‌ی حالت‌ها رو پوشش نمی‌دن؛ اینجا خودش می‌نویسه. */}
              <div className="menu-custom">
                <input className="input" placeholder={t('condOwnPlaceholder')} value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') custom(); }} />
                <button type="button" className="btn" disabled={!note.trim()} onClick={custom}>
                  {t('send')}
                </button>
              </div>
              <button type="button" className="menu-item dimmed" onClick={() => setCondMode(false)}>
                {t('back')}
              </button>
            </>
          ) : (
            <>
              <button type="button" role="menuitem" className="menu-item"
                onClick={() => pick({ r: 'in' })}>{t('imIn')}</button>
              <button type="button" role="menuitem" className="menu-item"
                onClick={() => pick({ r: 'out' })}>{t('notThisTime')}</button>
              <button type="button" role="menuitem" className="menu-item"
                onClick={() => pick({ r: 'maybe' })}>{t('maybe')}</button>
              <button type="button" role="menuitem" className="menu-item"
                onClick={() => setCondMode(true)}>
                {t('conditional')}<small>{t('conditionalHint')}</small>
              </button>
              <button type="button" role="menuitem" className="menu-item"
                onClick={() => pick({ r: 'out', why: 'not_my_thing' })}>
                {t('notMyThing')}<small>{t('notMyThingHint')}</small>
              </button>
              {answered ? (
                <button type="button" role="menuitem" className="menu-item danger"
                  onClick={() => pick({ r: 'pending' })}>{t('clearAnswer')}</button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
